import React, { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Popup,
  Segment,
  Statistic,
  Table,
} from 'semantic-ui-react';
import {
  ClientContractHubContractDto,
  ClientContractHubResponseDto,
  EContractStatus,
} from '../../services/contracts/ContractService';
import { formatCnpj } from '../../services/utils/stringUtils';
import { getContractStatusText } from '../../utils/contractStatusUtils';
import HubStatusFilterBar from './HubStatusFilterBar';
import {
  buildHubListItems,
  DEFAULT_HUB_STATUS_FILTER,
  filterHubListItems,
  filterTimelineForScope,
  formatHubUtc,
  getItemContractIds,
  HubCurrentStatusFilter,
  HubListItem,
  summarizeContracts,
} from './clientContractHubUtils';
import {
  getClientClassificationMeta,
  readClientClassification,
  readClientInternalNotes,
  readContractInternalNotes,
} from '../../utils/clientClassification';
import AbrirProtocoloPosVendaLink from '../posVenda/AbrirProtocoloPosVendaLink';
import ClientHubProtocolosTable from '../posVenda/ClientHubProtocolosTable';
import { readHubProtocolos } from '../../utils/posVenda/posVendaHubUtils';

interface ClientContractHubPanelProps {
  hub: ClientContractHubResponseDto;
  showClientHeader?: boolean;
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ContractSummaryCells: React.FC<{
  contracts: ClientContractHubContractDto[];
  groupLabel?: React.ReactNode;
}> = ({ contracts, groupLabel }) => {
  const summary = summarizeContracts(contracts);
  const statusSample = contracts[0]?.status;
  const contractsWithNotes = contracts
    .map((contract) => ({
      id: contract.id,
      tipo: contract.tipoContratacao || contract.contractType || contract.id.slice(0, 8),
      notes: readContractInternalNotes(contract),
    }))
    .filter((item) => item.notes.trim() !== '');

  return (
    <>
      <Table.Cell>
        {groupLabel ??
          (contracts.length === 1 ? (
            <>
              {contracts[0].tipoContratacao}
              {contracts[0].fastchip && (
                <Label size="mini" color="orange" style={{ marginLeft: 6 }}>
                  Fastchip
                </Label>
              )}
            </>
          ) : (
            <strong>Grupo ({summary.count} contratos)</strong>
          ))}
        {contractsWithNotes.length > 0 && (
          <Popup
            hoverable
            wide
            content={
              <div style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>
                {contractsWithNotes.map((item, idx) => (
                  <div key={item.id} style={{ marginBottom: idx < contractsWithNotes.length - 1 ? 8 : 0 }}>
                    <strong>{item.tipo}</strong>: {item.notes}
                  </div>
                ))}
              </div>
            }
            trigger={
              <Label size="tiny" color="blue" style={{ marginLeft: 8, cursor: 'help' }}>
                <Icon name="sticky note outline" />
                Obs. internas
              </Label>
            }
          />
        )}
      </Table.Cell>
      <Table.Cell>{formatHubUtc(summary.earliestCreated)}</Table.Cell>
      <Table.Cell>{summary.latestSigned ? formatHubUtc(summary.latestSigned) : '—'}</Table.Cell>
      <Table.Cell>
        {statusSample != null ? getContractStatusText(statusSample) : '—'}
        {contracts.length > 1 && <small> (+{summary.count - 1})</small>}
      </Table.Cell>
      <Table.Cell>{summary.totalLines}</Table.Cell>
      <Table.Cell>{formatCurrency(summary.totalValor)}</Table.Cell>
    </>
  );
};

export const ClientContractHubPanel: React.FC<ClientContractHubPanelProps> = ({
  hub,
  showClientHeader = true,
}) => {
  const [textFilter, setTextFilter] = useState('');
  const [statusFilter, setStatusFilter] =
    useState<HubCurrentStatusFilter>(DEFAULT_HUB_STATUS_FILTER);
  const [tipoFilter, setTipoFilter] = useState<string | undefined>(undefined);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [historyStatusFilter, setHistoryStatusFilter] = useState<EContractStatus[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const clientClassification = getClientClassificationMeta(
    readClientClassification(hub.client)
  );
  const clientInternalNotes = readClientInternalNotes(hub.client);
  const protocolosPosVenda = readHubProtocolos(hub);

  const allItems = useMemo(
    () => buildHubListItems(hub.contracts ?? []),
    [hub.contracts]
  );

  const filteredItems = useMemo(
    () =>
      filterHubListItems(allItems, {
        text: textFilter,
        statuses: statusFilter,
        tipo: tipoFilter,
        historyStatuses: historyStatusFilter,
        timeline: hub.timeline ?? [],
      }),
    [allItems, textFilter, statusFilter, tipoFilter, historyStatusFilter, hub.timeline]
  );

  const visibleContractIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of filteredItems) {
      getItemContractIds(item).forEach((id) => ids.add(id));
    }
    return ids;
  }, [filteredItems]);

  const totalContractCount = hub.contracts?.length ?? 0;
  const visibleContractCount = visibleContractIds.size;

  const scopedTimeline = useMemo(
    () =>
      filterTimelineForScope(hub.timeline ?? [], visibleContractIds, selectedIds),
    [hub.timeline, visibleContractIds, selectedIds]
  );

  const scopedContractIds = useMemo(() => {
    if (selectedIds.size > 0) return selectedIds;
    return visibleContractIds;
  }, [selectedIds, visibleContractIds]);

  const scopedContracts = useMemo(
    () => (hub.contracts ?? []).filter((c) => scopedContractIds.has(c.id)),
    [hub.contracts, scopedContractIds]
  );

  const scopedLines = useMemo(
    () => (hub.lines ?? []).filter((l) => scopedContractIds.has(l.contractId)),
    [hub.lines, scopedContractIds]
  );

  const scopedSummary = useMemo(() => summarizeContracts(scopedContracts), [scopedContracts]);

  const statusOptions = useMemo(
    () =>
      Object.entries(EContractStatus)
        .filter(([, value]) => typeof value === 'number')
        .map(([, value]) => ({
          key: String(value),
          value: value as number,
          text: getContractStatusText(value as EContractStatus),
        })),
    []
  );

  const tipoOptions = useMemo(() => {
    const tipos = new Set((hub.contracts ?? []).map((c) => c.tipoContratacao));
    return Array.from(tipos)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((t) => ({ key: t, value: t, text: t }));
  }, [hub.contracts]);

  const toggleContract = (contractId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(contractId);
      else next.delete(contractId);
      return next;
    });
  };

  const toggleGroup = (item: HubListItem & { kind: 'group' }, checked: boolean) => {
    const ids = getItemContractIds(item);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const isGroupChecked = (item: HubListItem & { kind: 'group' }) => {
    const ids = getItemContractIds(item);
    return ids.length > 0 && ids.every((id) => selectedIds.has(id));
  };

  const isGroupIndeterminate = (item: HubListItem & { kind: 'group' }) => {
    const ids = getItemContractIds(item);
    const selected = ids.filter((id) => selectedIds.has(id)).length;
    return selected > 0 && selected < ids.length;
  };

  const toggleGroupExpanded = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const renderItemRow = (item: HubListItem) => {
    if (item.kind === 'contract') {
      const c = item.contract;
      return (
        <Table.Row key={c.id} positive={selectedIds.has(c.id)}>
          <Table.Cell collapsing>
            <Checkbox
              checked={selectedIds.has(c.id)}
              onChange={(_, d) => toggleContract(c.id, !!d.checked)}
            />
          </Table.Cell>
          <ContractSummaryCells contracts={[c]} />
        </Table.Row>
      );
    }

    const expanded = expandedGroups.has(item.groupId);
    const groupChecked = isGroupChecked(item);
    const indeterminate = isGroupIndeterminate(item);

    return (
      <React.Fragment key={item.groupId}>
        <Table.Row positive={groupChecked}>
          <Table.Cell collapsing>
            <Checkbox
              checked={groupChecked}
              indeterminate={indeterminate}
              onChange={(_, d) => toggleGroup(item, !!d.checked)}
            />
          </Table.Cell>
          <ContractSummaryCells
            contracts={item.members}
            groupLabel={
              <button
                type="button"
                onClick={() => toggleGroupExpanded(item.groupId)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontWeight: 600,
                }}
              >
                <Icon name={expanded ? 'chevron down' : 'chevron right'} />
                Grupo ({item.members.length} contratos)
              </button>
            }
          />
        </Table.Row>
        {expanded &&
          item.members.map((member) => (
            <Table.Row key={member.id} style={{ background: 'rgba(0,0,0,0.02)' }}>
              <Table.Cell />
              <Table.Cell>
                <Checkbox
                  checked={selectedIds.has(member.id)}
                  onChange={(_, d) => toggleContract(member.id, !!d.checked)}
                  style={{ marginRight: 8 }}
                />
                {member.tipoContratacao}
                {member.fastchip && (
                  <Label size="mini" color="orange" style={{ marginLeft: 6 }}>
                    Fastchip
                  </Label>
                )}
              </Table.Cell>
              <Table.Cell>{formatHubUtc(member.createdAt)}</Table.Cell>
              <Table.Cell>
                {member.dataAssinatura ? formatHubUtc(member.dataAssinatura) : '—'}
              </Table.Cell>
              <Table.Cell>
                {member.status != null ? getContractStatusText(member.status) : '—'}
              </Table.Cell>
              <Table.Cell>{member.lineCount}</Table.Cell>
              <Table.Cell>{formatCurrency(member.valorTotalMensal)}</Table.Cell>
            </Table.Row>
          ))}
      </React.Fragment>
    );
  };

  return (
    <div>
      {showClientHeader && hub.client && (
        <Segment>
          <Header as="h3">{hub.client.razaoSocial}</Header>
          <p>
            {formatCnpj(hub.client.cnpj)}
            {hub.client.cidade ? ` — ${hub.client.cidade}/${hub.client.estado ?? ''}` : ''}
          </p>
          <Label color={clientClassification.color} size="small">
            <Icon name="tag" />
            {clientClassification.label}
          </Label>
          {hub.client.createdAt && (
            <p>
              <Icon name="calendar plus" />
              Cadastrado em: {formatHubUtc(hub.client.createdAt)}
            </p>
          )}
          {hub.client.administratorNome && (
            <p>
              Administrador: {hub.client.administratorNome}
              {hub.client.administratorCpf ? ` (${hub.client.administratorCpf})` : ''}
            </p>
          )}
          {clientInternalNotes && (
            <Message info size="small" style={{ marginTop: 10, marginBottom: 0 }}>
              <Message.Header>Observações internas do cliente</Message.Header>
              <p style={{ whiteSpace: 'pre-wrap' }}>{clientInternalNotes}</p>
            </Message>
          )}
          <div style={{ marginTop: 12 }}>
            <AbrirProtocoloPosVendaLink clientId={hub.client.id} />
          </div>
        </Segment>
      )}

      {hub.client && protocolosPosVenda.length > 0 && (
        <Segment>
          <Header as="h4">Protocolos de pós-venda</Header>
          <ClientHubProtocolosTable clientId={hub.client.id} protocolos={protocolosPosVenda} />
        </Segment>
      )}

      <Segment>
        <HubStatusFilterBar
          value={statusFilter}
          onChange={setStatusFilter}
          totalContracts={totalContractCount}
          visibleContracts={visibleContractCount}
        />
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Buscar na lista</label>
              <Input
                icon="search"
                placeholder="Tipo, token, ID do contrato..."
                value={textFilter}
                onChange={(_, d) => setTextFilter(d.value)}
                action={
                  textFilter
                    ? {
                        icon: 'close',
                        onClick: () => setTextFilter(''),
                      }
                    : undefined
                }
              />
            </Form.Field>
            <Form.Field>
              <label>Tipo de contratação</label>
              <Dropdown
                selection
                clearable
                options={tipoOptions}
                value={tipoFilter}
                onChange={(_, d) => setTipoFilter(d.value as string | undefined)}
                placeholder="Todos os tipos"
              />
            </Form.Field>
            <Form.Field>
              <label>&nbsp;</label>
              <Button
                type="button"
                basic
                fluid
                onClick={() => setShowAdvancedFilters((v) => !v)}
              >
                <Icon name={showAdvancedFilters ? 'angle up' : 'angle down'} />
                Filtros avançados
                {historyStatusFilter.length > 0 && (
                  <Label circular color="orange" style={{ marginLeft: 8 }}>
                    {historyStatusFilter.length}
                  </Label>
                )}
              </Button>
            </Form.Field>
          </Form.Group>
          {showAdvancedFilters && (
            <Form.Field>
              <label>Passou por todos estes status (AND)</label>
              <Dropdown
                selection
                multiple
                search
                options={statusOptions}
                value={historyStatusFilter}
                onChange={(_, d) =>
                  setHistoryStatusFilter((d.value as EContractStatus[]) ?? [])
                }
                placeholder="Ex.: Criado e Ativo — opcional"
              />
            </Form.Field>
          )}
        </Form>
      </Segment>

      {filteredItems.length === 0 && totalContractCount > 0 && (
        <Message warning size="small">
          Nenhum contrato corresponde aos filtros atuais. Tente <strong>Todos</strong> nos atalhos de
          status ou ajuste a seleção múltipla.
        </Message>
      )}

      <Segment>
        <Header as="h4">
          Contratos e grupos
          <Header.Subheader>
            {filteredItems.length} unidade(s) na lista ·{' '}
            {selectedIds.size > 0
              ? `${selectedIds.size} selecionado(s) — timeline filtrada`
              : 'marque contratos para focar a timeline, ou deixe vazio para ver tudo do recorte'}
          </Header.Subheader>
        </Header>
        <Table celled compact selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Unidade</Table.HeaderCell>
              <Table.HeaderCell>Criado em</Table.HeaderCell>
              <Table.HeaderCell>Assinatura</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Linhas</Table.HeaderCell>
              <Table.HeaderCell>Valor mensal</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredItems.map((item) => renderItemRow(item))}
            {filteredItems.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={7} textAlign="center">
                  Nenhum contrato corresponde aos filtros.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Segment>

      <Segment>
        <Statistic.Group size="mini" widths={6}>
          <Statistic label="Contratos" value={scopedSummary.count} />
          <Statistic label="Cancelados" value={scopedSummary.cancelledCount} />
          <Statistic label="Linhas" value={scopedSummary.totalLines} />
          <Statistic label="Fastchip" value={scopedSummary.fastchipCount} />
          <Statistic
            label="Suspensão pendente"
            value={scopedLines.filter((l) => l.requerSuspensao).length}
          />
          <Statistic label="Suspensas" value={scopedLines.filter((l) => l.estaSuspensa).length} />
        </Statistic.Group>
      </Segment>

      <Segment>
        <Header as="h4">Timeline</Header>
        <Table celled compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Quando</Table.HeaderCell>
              <Table.HeaderCell>Evento</Table.HeaderCell>
              <Table.HeaderCell>Contrato</Table.HeaderCell>
              <Table.HeaderCell>Detalhe</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {scopedTimeline.map((event, index) => (
              <Table.Row key={`${event.contractId}-${event.occurredAtUtc}-${index}`}>
                <Table.Cell>{formatHubUtc(event.occurredAtUtc)}</Table.Cell>
                <Table.Cell>
                  {event.eventTitle ??
                    (event.eventStatus != null
                      ? getContractStatusText(event.eventStatus)
                      : event.eventType)}
                  {event.isInferred && (
                    <Label size="mini" color="grey" style={{ marginLeft: 6 }}>
                      estimado
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {event.eventType === 'ClientRegistered'
                    ? 'Cliente'
                    : (event.contractTipo ?? event.contractId.slice(0, 8))}
                </Table.Cell>
                <Table.Cell>
                  {event.eventDetail ??
                    event.changeReason ??
                    (event.previousStatus != null
                      ? `De ${getContractStatusText(event.previousStatus)}`
                      : '—')}
                </Table.Cell>
              </Table.Row>
            ))}
            {scopedTimeline.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center">
                  Sem eventos para o recorte atual.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Segment>

      <Segment>
        <Header as="h4">Linhas do recorte</Header>
        <Table celled compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>DDD/Número</Table.HeaderCell>
              <Table.HeaderCell>ICCID</Table.HeaderCell>
              <Table.HeaderCell>Contrato</Table.HeaderCell>
              <Table.HeaderCell>Suspensão</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {scopedLines.map((line) => (
              <Table.Row key={line.lineId}>
                <Table.Cell>
                  {line.ddd ?? '—'} {line.numero ?? ''}
                  <br />
                  <small>{line.planName ?? ''}</small>
                </Table.Cell>
                <Table.Cell>{line.chipCcid ?? '—'}</Table.Cell>
                <Table.Cell>{line.contractTipo ?? line.contractId.slice(0, 8)}</Table.Cell>
                <Table.Cell>
                  {line.estaSuspensa
                    ? 'Suspensa'
                    : line.requerSuspensao
                      ? 'Pendente'
                      : '—'}
                </Table.Cell>
              </Table.Row>
            ))}
            {scopedLines.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center">
                  Sem linhas no recorte atual.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Segment>
    </div>
  );
};

export default ClientContractHubPanel;
